import { useState } from 'react';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { Badge, Card, EmptyState, FilterBar, SelectField, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { useRequests } from './api';

type Filter = 'open' | 'approved' | 'refused' | 'completed' | 'all';
const FILTERS: Filter[] = ['open', 'approved', 'refused', 'completed', 'all'];

/** Placeholder queue for self-service requests (S52): read-only list. Decisions and templates arrive with S52. */
export function RequestsTab() {
  const [status, setStatus] = useState<Filter>('open');
  const q = useRequests(status);
  return (
    <Card title={t('p4dp.requests.title')} description={t('p4dp.requests.help')}>
      <FilterBar columns="sm:grid-cols-3">
        <SelectField label={t('p4dp.requests.status')} value={status} onChange={(e) => setStatus(e.target.value as Filter)}>
          {FILTERS.map((f) => (
            <option key={f} value={f}>
              {t(`p4dp.requests.status.${f}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.items.length === 0 ? (
        <EmptyState message={t('p4dp.requests.empty')} />
      ) : (
        <TableWrap caption={t('p4dp.requests.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('p4dp.requests.col.ref')}</Th>
              <Th>{t('p4dp.requests.col.kind')}</Th>
              <Th>{t('p4dp.requests.col.name')}</Th>
              <Th>{t('p4dp.requests.col.received')}</Th>
              <Th>{t('p4dp.requests.col.due')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.items.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-2.5 font-medium">{r.reference}</td>
                <td className="px-4 py-2.5">{t(`p4dp.requests.kind.${r.kind}`)}</td>
                <td className="px-4 py-2.5">{r.name}</td>
                <td className="px-4 py-2.5">{formatDateTime(r.receivedAt)}</td>
                <td className="px-4 py-2.5">
                  {r.daysLeft === null ? null : (
                    <Badge tone={r.daysLeft < 0 ? 'bad' : r.daysLeft <= 5 ? 'warn' : 'neutral'}>
                      {r.daysLeft < 0 ? t('p4dp.requests.overdue', { days: -r.daysLeft }) : t('p4dp.requests.daysLeft', { days: r.daysLeft })}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Card>
  );
}
